import { useState, useEffect, useCallback } from 'react';

export type RawSearchResult = {
    type: 'search_result';
    id: string;
    attributes: {
        title: string;
        body_excerpt: string;
        url: string;
        score: number;
        highlight: {
            title?: string[] | null;
            body?: string[] | null;
        };
    };
};

type SearchResultInstancesTargetSchema = {
    data: RawSearchResult[];
    meta: {
        total_count: number;
    };
};

const API_URL = 'https://site-api.datocms.com/search-results';

export default function useSiteSearch(
    query: string | string[] | undefined,
    locale: string,
    buildTriggerId: string,
) {
    const [thisQuery, setThisQuery] = useState<string | string[] | undefined>(
        query,
    );
    const [error, setError] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [response, setResponse] = useState<
        SearchResultInstancesTargetSchema | undefined
    >();

    useEffect(() => {
        async function fetchResults(query: string) {
            try {
                setIsLoading(true);
                setError(undefined);

                if (!query) {
                    setResponse({ data: [], meta: { total_count: 0 } });
                    return;
                }

                let URL = `${API_URL}?filter[fuzzy]=false&filter[query]=${thisQuery}&filter[locale]=${locale}&page[limit]=100`;

                if (buildTriggerId) {
                    URL += `&filter[build_trigger_id]=${buildTriggerId}`;
                }

                const res = await fetch(URL, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-Api-Version': '3',
                        Authorization: `Bearer ${process.env.GATSBY_SITE_SEARCH_API_KEY}`,
                    },
                });

                if (!res || res.status !== 200) {
                    throw new Error('No response');
                }

                let data = await res.json();

                setResponse(data);
            } catch (e) {
                if (e instanceof Error) {
                    setError(e.message);
                } else {
                    setError('Unknown error!');
                }

                console.error(e);
            } finally {
                setIsLoading(false);
            }
        }

        fetchResults(thisQuery as string);
    }, [thisQuery, locale, buildTriggerId]);

    const publicSetQuery = useCallback(
        (newQuery?: string) => {
            setThisQuery(newQuery);
        },
        [setThisQuery],
    );

    return {
        response,
        error,
        setQuery: publicSetQuery,
        isLoading,
        query: thisQuery,
    } as const;
}
